import TrailwiseEventBus from '../TrailwiseEventBus';
import { TwuidSearchSuccessPayload, UsrnSearchSuccessPayload } from "../TrailwiseEvents";
import { TwuidSearchService } from "../TwuidSearchService";
import { MapViewStore } from "../MapViewStore";
import { MapFocusConfig } from "./MapConfig";
import { MapNavigator } from "./MapNavigator";
import { OverlayController } from "./OverlayController";
import { FeatureInfoController } from "./FeatureInfoController";

/**
 * Reacts to TWUID/USRN search successes (zoom, layer enable, highlight,
 * feature info), owns the bookmark "show on map" links, the auto-close-search
 * preference and the initial share/focus flow.
 */
export class SearchCoordinator {

    private _focusConfig: MapFocusConfig | null;

    public constructor(
        private _mapStore: MapViewStore,
        private _navigator: MapNavigator,
        private _overlays: OverlayController,
        private _featureInfo: FeatureInfoController,
        private _twuidSearchService: TwuidSearchService,
        focusConfig: MapFocusConfig | null
    ) {
        this._focusConfig = focusConfig;

        TrailwiseEventBus.twuidSearchSuccess.on(this.handleTwuidSearchSuccess);
        TrailwiseEventBus.usrnSearchSuccess.on(this.handleUsrnSearchSuccess);

        this.initBookmarks();
        this.initAutoCloseSearch();
    }

    public applyInitialFocus = (): void => {
        if (!this._focusConfig || !this._focusConfig.twuid) {
            return;
        }

        if (this._focusConfig.routeType) {
            this._overlays.ensureLayerVisibleByName(this._focusConfig.routeType);
        }

        this._twuidSearchService.search(this._focusConfig.twuid, "focus")
            .catch(() => null);

        this._focusConfig = null;
    }

    private initAutoCloseSearch = (): void => {
        let self = this;

        if (self._mapStore.getMapInfo().closePanelOnSearchSuccess === true) {
            $("#ckAutoCloseSearch").prop("checked", true);
        }

        $("#ckAutoCloseSearch").on("click", function () {
            self._mapStore.onAutoCloseSearchChanged($(this).prop("checked"));
        });
    }

    private closeSearchPanelIfEnabled = (): void => {
        if (this._mapStore.getMapInfo().closePanelOnSearchSuccess === true) {
            // Check if search panel is currently open
            if ($("#slide-panel-search").is(':visible')) {
                $("#slide-panel-btn-search").trigger("click");
            }
        }
    }

    private handleTwuidSearchSuccess = (payload: TwuidSearchSuccessPayload): void => {
        if (!payload || !payload.result) {
            return;
        }

        const { result } = payload;
        if (result.routeType) {
            this._overlays.ensureLayerVisibleByName(result.routeType);
        }

        if (result.wkt) {
            this._navigator.fitMapToWkt(result.wkt);
            //instant highlight while the feature info request runs
            this._featureInfo.highlightRouteGuids([result.routeGUID]);

            const coordinate = this._navigator.getCoordinateFromWkt(result.wkt);
            if (coordinate) {
                //same path as a map click: auto-detected query layers plus highlight/comment-count callback
                this._featureInfo.showFeaturesAtCoordinate(coordinate);
            }
        }

        // Close search panel if auto-close is enabled
        this.closeSearchPanelIfEnabled();
    }

    private handleUsrnSearchSuccess = (payload: UsrnSearchSuccessPayload): void => {
        if (!payload || !payload.result) {
            return;
        }

        this._overlays.ensureLayerVisibleByName("OpenUSRN");
        if (payload.result.extent) {
            this._navigator.fitMapToExtent(payload.result.extent);
        }
        if (payload.result.coordinate) {
            this._navigator.ensureMinimumZoom(payload.result.coordinate, 15, () => {
                this._featureInfo.showFeatureInfoForLayer(payload.result.coordinate, payload.result.layerName);
            });
        } else {
            this._featureInfo.showFeatureInfoForLayer(payload.result.coordinate, payload.result.layerName);
        }

        // Close search panel if auto-close is enabled
        this.closeSearchPanelIfEnabled();
    }

    private initBookmarks = (): void => {
        let self = this;

        $(".showOnMap").on("click", function () {
            let twuid = $(this).data("twuid");
            $("#twuidLoading").show();
            self._twuidSearchService.search(twuid, "favourite")
                .catch(() => null)
                .then(function () {
                    $("#twuidLoading").hide();
                });
        });
    }
}
